import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';

const UIContext = createContext();

export const UIProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);
    const [confirmModal, setConfirmModal] = useState(null);

    const showToast = useCallback((message, type = 'info', title = null) => {
        const id = Math.random().toString(36).substr(2, 9);
        const newToast = { id, message, type, title };
        setToasts(prev => [...prev, newToast]);

        // Auto remove
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 5000);
    }, []);

    const showConfirm = useCallback((config) => {
        return new Promise((resolve) => {
            setConfirmModal({
                ...config,
                resolve: (value) => {
                    setConfirmModal(null);
                    resolve(value);
                }
            });
        });
    }, []);

    return (
        <UIContext.Provider value={{ showToast, showConfirm }}>
            {children}

            {/* Global Toasts Container */}
            <div className="premium-toast-container">
                {toasts.map(toast => (
                    <div key={toast.id} className={`premium-toast toast-${toast.type}`}>
                        <div className="toast-icon">
                            {toast.type === 'success' && <CheckCircle2 size={24} />}
                            {toast.type === 'error' && <AlertCircle size={24} />}
                            {toast.type === 'info' && <Info size={24} />}
                        </div>
                        <div className="toast-content">
                            {toast.title && <div className="toast-title">{toast.title}</div>}
                            <div className="toast-message">{toast.message}</div>
                        </div>
                        <button
                            className="toast-close"
                            onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                            style={{
                                opacity: 0.5,
                                padding: '4px',
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                color: 'inherit'
                            }}
                        >
                            <X size={16} />
                        </button>
                    </div>
                ))}
            </div>

            {/* Global Confirmation Modal */}
            {confirmModal && (
                <div className="premium-confirm-overlay" onClick={() => confirmModal.resolve(false)}>
                    <div className="premium-confirm-card" onClick={e => e.stopPropagation()}>
                        <div className="confirm-icon-box">
                            <AlertCircle size={32} />
                        </div>
                        <h3 className="confirm-title">{confirmModal.title || 'Confirm Action'}</h3>
                        <p className="confirm-message">{confirmModal.message}</p>
                        <div className="confirm-actions">
                            <button className="btn-secondary" onClick={() => confirmModal.resolve(false)}>
                                {confirmModal.cancelText || 'Cancel'}
                            </button>
                            <button className="btn-primary" onClick={() => confirmModal.resolve(true)}>
                                {confirmModal.confirmText || 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </UIContext.Provider>
    );
};

export const useUI = () => {
    const context = useContext(UIContext);
    if (!context) {
        throw new Error('useUI must be used within a UIProvider');
    }
    return context;
};
